import { useEffect, useRef, useState } from 'react';
import {
  HashRouter, Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import { SessionProvider, hasUnread, useActiveRide, usePreorders, useSession } from './session';
import { DraftCtx, type Draft, aboard, emptyDraft, isBlank } from './features/draft';
import { LoginScreen } from './features/login';
import { ConsentScreen } from './features/consent';
import { holdsConsent } from './consent';
import { TownScreen } from './features/town';
import { RideTab } from './features/ride';
import { OrdersScreen } from './features/orders';
import { AddressesScreen } from './features/addresses';
import { ProfileScreen } from './features/profile';
import { SettingsScreen, AboutScreen } from './features/settings';
import { SupportScreen, SupportThreadScreen } from './features/support';
import { ChatScreen } from './features/chat';
import { useFollowDaylight, useNight, usePreloadPictures } from './daylight';
import { useDark } from './theme';
import { NavIcon, type NavGlyph } from './icons';
import { cachedOrderPhone } from './api';
import { Icon } from './material';
import { Button, EmptyCard, Spinner, Toasts } from './widgets';
import { dismissNewBuild, reloadNow, useHoldReload, useNewBuild } from './update';

export function App() {
  return (
    <SessionProvider>
      {/*
        A hash router, for the same reason the panel uses one: this is a static
        bundle that has to work wherever it is dropped, and every other kind of
        routing needs a rewrite rule configured somewhere for a deep link not to
        404. It also survives being opened from a home-screen icon, where the
        start URL is whatever iOS saved.
      */}
      <HashRouter>
        <Root />
      </HashRouter>
      <Toasts />
    </SessionProvider>
  );
}

/**
 * Five states, decided in one place, as `RiderApp` decides them: signed out, a
 * session that could not be checked because the server did not answer, signed in
 * with «Согласие» to give, signed in without a town, and the app proper. The consent
 * comes first: the town is the passenger's data too. A town has to come before
 * anything else after it — the address picker and the tariff card have nothing to
 * show without one.
 *
 * «Согласие» is asked of an account the box at «Регистрация» did not reach, once,
 * in front of everything — but not over a ride, which keeps the screen until
 * «Готово» lets it go, and not on a guess while the rides have not come back. One
 * ticked here and still on its way to the server counts as given (ADR 0030).
 */
function Root() {
  const { me, restoring, unreachable, ridesKnown, ridesFailed } = useSession();
  const ride = useActiveRide();
  if (restoring) return <Spinner />;
  if (unreachable) return <Unreachable />;
  if (!me) return <LoginScreen />;
  const due = me.consentDue;
  if (due && !holdsConsent(me.id, due)) {
    if (!ridesKnown && !ridesFailed) return <Spinner />;
    if (!ride) return <ConsentGate />;
  }
  if (!me.passenger?.townId) return <TownScreen />;
  return <Shell />;
}

/**
 * «Согласие», and «Поддержка» behind it for a link that leads there. «Удалить аккаунт»
 * is a sheet on «Согласие» itself (ADR 0048).
 */
function ConsentGate() {
  return (
    <Routes>
      <Route path="/support" element={<SupportScreen />} />
      <Route path="/support/:threadId" element={<SupportThreadScreen />} />
      <Route path="*" element={<ConsentScreen />} />
    </Routes>
  );
}

/**
 * The session is still on this device; the server just did not answer. «Повторить»
 * asks again, and the last number the server gave, when there is one, orders a car
 * by phone meanwhile — nothing here signs anybody out.
 */
function Unreachable() {
  const { retry } = useSession();
  const phone = cachedOrderPhone();
  return (
    <div className="unreachable column">
      <div className="unreachable-main">
        <EmptyCard
          title="Нет связи с сервером"
          hint="Вы остаётесь в аккаунте. Проверьте подключение и попробуйте ещё раз."
        />
        <div style={{ marginTop: 16 }}>
          <Button onClick={retry}>Повторить</Button>
        </div>
        {phone && (
          <a className="btn tint unreachable-phone" href={`tel:${phone}`}>
            <Icon name="phone_in_talk_rounded" size={27} />
            Сделать заказ по телефону
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * «Доступна новая версия», over the tabs until it is taken or put off — one notice, as
 * the phone app's in the tray. A strip in the column rather than a toast over it: it
 * stays, and what it would stand over is «Заказать».
 */
function UpdateNote() {
  const { offered } = useNewBuild();
  if (!offered) return null;
  return (
    <div className="update-note" role="status">
      <div className="update-note-inner column">
        <span className="grow">Доступна новая версия</span>
        <button type="button" className="update-note-go" onClick={reloadNow}>
          Обновить
        </button>
        <button
          type="button"
          className="update-note-off"
          aria-label="Не сейчас"
          title="Не сейчас"
          onClick={dismissNewBuild}
        >
          <Icon name="close" size={18} />
        </button>
      </div>
    </div>
  );
}

/** The four tabs, in the phone's order and under the phone's words. */
const TABS: { to: string; glyph: NavGlyph; label: string }[] = [
  { to: '/', glyph: 'booking', label: 'Заказ' },
  { to: '/orders', glyph: 'orders', label: 'Поездки' },
  { to: '/places', glyph: 'addresses', label: 'Адреса' },
  { to: '/profile', glyph: 'profile', label: 'Профиль' },
];

/**
 * Which tab a path is on. A screen pushed over the tabs belongs to the tab it was
 * opened from — «Настройки» and «Поддержка» to «Профиль», a chat to the ride — and
 * that is the tab left under it.
 */
function tabOf(path: string): number {
  if (path.startsWith('/orders')) return 1;
  if (path.startsWith('/places')) return 2;
  if (path.startsWith('/profile') || path.startsWith('/settings') || path.startsWith('/support')) {
    return 3;
  }
  return 0;
}

function Shell() {
  const { api, me, rides, supportUnread } = useSession();
  const { pathname } = useLocation();
  // Held here rather than in the booking screen: opening the address picker,
  // wandering off to «Мои адреса» and coming back must not lose a half-typed order.
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  // The «Сейчас» ride the draft went out as, until the passenger is aboard it.
  const [sentAs, setSentAs] = useState<string | null>(null);
  const ride = useActiveRide();

  useEffect(() => {
    if (!aboard(ride, sentAs)) return;
    setDraft(emptyDraft);
    setSentAs(null);
  }, [ride, sentAs]);

  // A new build waits for «Обновить» while there is an order half typed or a ride
  // under way, rather than loading itself over them (`update.ts`).
  useHoldReload(!isBlank(draft) || ride !== null);

  // Which of each pair of streets heads a ride — asked now, again at each sunrise
  // and sunset, and again when «Мой город» moves.
  useFollowDaylight(api, me?.passenger?.townId ?? null);
  usePreloadPictures(useNight(), useDark());

  const active = tabOf(pathname);
  // The tab arriving fades in, as a route pushed on top would. Every tab stays
  // built, so it is a class on the way in rather than a remount.
  const previous = useRef(active);
  const [arriving, setArriving] = useState<number | null>(null);
  useEffect(() => {
    if (previous.current !== active) setArriving(active);
    previous.current = active;
  }, [active]);

  // The driver wrote and it is not read yet: «Заказ», where the ride and its «Чат»
  // are. An answer from «Поддержка»: «Профиль», where its row is.
  // A предзаказ's driver wrote: «Поездки», where its card and «Чат» are.
  const planned = new Set(usePreorders().map((r) => r.id));
  const dots = [
    rides.some((r) => hasUnread(r) && !planned.has(r.id)),
    rides.some((r) => hasUnread(r) && planned.has(r.id)),
    false,
    supportUnread > 0,
  ];
  const dotLabels = [
    'Водитель написал в чат',
    'Водитель написал в чат',
    '',
    'Есть непрочитанные ответы поддержки',
  ];

  const panes = [<RideTab key="ride" />, <OrdersScreen key="orders" />, <AddressesScreen key="places" />, <ProfileScreen key="profile" />];

  return (
    <DraftCtx.Provider
      value={{
        draft,
        set: (patch) => setDraft((current) => ({ ...current, ...patch })),
        sent: setSentAs,
      }}
    >
      <div className="shell">
        <Legacy />
        <div className="topbar">
          <div className="topbar-inner column">
            <span className="brand">Лимузин</span>
            <nav className="toptabs">
              {TABS.map((tab, i) => (
                <Link key={tab.to} to={tab.to} className={i === active ? 'toptab on' : 'toptab'}>
                  <span className="dotted">
                    <NavIcon glyph={tab.glyph} on={i === active} />
                    {dots[i] && <span className="unread-dot" aria-label={dotLabels[i]} />}
                  </span>
                  {tab.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <div className="panes">
          {panes.map((pane, i) => (
            <div
              key={i}
              className={arriving === i ? 'pane arriving' : 'pane'}
              hidden={i !== active}
              onAnimationEnd={() => setArriving(null)}
            >
              {pane}
            </div>
          ))}
        </div>

        <UpdateNote />
        <nav className="tabbar">
          {TABS.map((tab, i) => (
            <Link
              key={tab.to}
              to={tab.to}
              className={i === active ? 'tab on' : 'tab'}
              aria-current={i === active ? 'page' : undefined}
            >
              <span className="dotted">
                <NavIcon glyph={tab.glyph} on={i === active} />
                {dots[i] && <span className="unread-dot" aria-label={dotLabels[i]} />}
              </span>
              <span>{tab.label}</span>
            </Link>
          ))}
        </nav>

        {/* Screens pushed over the tabs, and over the bar, as a route is on a phone. */}
        <Routes>
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/settings/app" element={<AboutScreen />} />
          <Route path="/support" element={<SupportScreen />} />
          <Route path="/support/:threadId" element={<SupportThreadScreen />} />
          <Route path="/chat/:rideId" element={<ChatScreen />} />
          {/* A предзаказ's: over «Поездки», where its card and its «Чат» are. */}
          <Route path="/orders/chat/:rideId" element={<ChatScreen />} />
          <Route path="/" element={null} />
          <Route path="/orders" element={null} />
          <Route path="/places" element={null} />
          <Route path="/profile" element={null} />
          {/* «Удалить аккаунт», a sheet over «Профиль» (ADR 0048). */}
          <Route path="/profile/remove" element={null} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </DraftCtx.Provider>
  );
}

/**
 * The links notifications have always carried — `#/?chat=<ride>` and
 * `#/profile?thread=<thread>`, which the server's Web Push sends too — taken to the
 * screens they name now.
 */
function Legacy() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const chat = params.get('chat');
  const thread = params.get('thread');
  // A предзаказ's chat opens over «Поездки», where its card is.
  const planned = usePreorders().some((r) => r.id === chat);
  useEffect(() => {
    if (pathname === '/' && chat) {
      navigate(`${planned ? '/orders' : ''}/chat/${encodeURIComponent(chat)}`, { replace: true });
    }
    if (pathname === '/profile' && thread) {
      navigate(`/support/${encodeURIComponent(thread)}`, { replace: true });
    }
  }, [pathname, chat, planned, thread, navigate]);
  return null;
}
